<script setup lang="ts">
const ratingsForm = reactive({
	stars: '',
	ratings: '',
	ratingsCount: '',
	url: '',
})

const { locale } = useI18n()
const pageTestimonialsSection = inject<Ref<ICMSLocationContent>>('pageTestimonialsSection')!
const sectionContent = ref<ICMSLocationContentTranslations>(
	pageTestimonialsSection.value?.translations.find((translation) => translation.language === locale.value)?.content,
)
const assignedLocations = inject<Ref<ILocationModel[]>>('assignedLocations')!
const googleRatingsStore = useGoogleRatingsStore()

watch(
	() => assignedLocations.value,
	(v) => {
		if (v[0] && v[0].countRatings && v[0].googleUrl && v[0].ratings) {
			ratingsForm.stars = v[0].ratings
			ratingsForm.ratings = v[0].ratings
			ratingsForm.ratingsCount = v[0].countRatings
			ratingsForm.url = v[0].googleUrl
		} else {
			// branch has no Google reviews of its own - fall back to the company-wide reviews (same as the home page)
			googleRatingsStore.fetchGoogleRating()
		}
	},
	{ deep: true, immediate: true },
)

watch(
	() => googleRatingsStore.getGoogleRating,
	(v) => {
		if (v && !ratingsForm.stars) {
			ratingsForm.stars = String(v.stars)
			ratingsForm.ratings = String(v.ratings)
			ratingsForm.ratingsCount = String(v.ratingsCount)
			ratingsForm.url = v.url
		}
	},
	{ immediate: true },
)

const isAtleastOneTestimonialFilled = computed(() => {
	if (sectionContent.value && sectionContent.value?.length > 0) {
		return sectionContent.value.some((testimonial) => testimonial.text !== '' && testimonial.author !== '' && testimonial.stars !== '')
	}
	return false
})
</script>

<template>
	<section v-if="isAtleastOneTestimonialFilled" class="testimonials">
		<div class="testimonials__container">
			<GlobalImage class="testimonials__image" src="testimonials-image.png" alt="testimonials-image" />
			<div class="testimonials__image-shadow"></div>
			<h2
				data-aos="fade-up"
				class="testimonials__title text-display"
				v-html="detectNewline($t('modules.home_page_module.testimonials.opinions'))"></h2>
			<Swiper
				class="testimonials__swiper"
				:slides-per-view="'auto'"
				:centered-slides="true"
				space-between="140"
				:loop="true"
				:loop-additional-slides="1"
				:watch-slides-progress="true"
				@after-init="
					(swiper) => {
						useTimeoutFn(() => {
							swiper.update()
						}, 400)
					}
				">
				<SwiperSlide v-for="(slide, idx) in sectionContent" :key="idx" class="testimonials__swiper__slide">
					<TestimonialsItem :index="idx" :text="slide.text" :author="slide.author" :stars="slide.stars" />
				</SwiperSlide>
				<TestimonialsSwiperNav />
			</Swiper>
			<div class="testimonials__gradient testimonials__gradient--left"></div>
			<div class="testimonials__gradient testimonials__gradient--right"></div>
		</div>
		<div class="reviews">
			<div v-if="ratingsForm.stars && ratingsForm.stars !== ''" class="google-review">
				<div class="google-review__logo">
					<GlobalIcon name="google-icon" :height="16" />
				</div>
				<p data-aos="fade-up" class="google-review__text">
					{{ $t('modules.home_page_module.testimonials.assessment') }} {{ formatRating(ratingsForm.ratings) }}
					{{ $t('modules.home_page_module.testimonials.of') }}
					<a :href="ratingsForm?.url" target="_blank" rel="nofollow" class="google-review__link">
						{{ ratingsForm.ratingsCount }} {{ $t('modules.home_page_module.testimonials.opinions_1') }}
					</a>
					GOOGLE
				</p>
			</div>
			<LocationTrustpilotReviews variant="bar" />
		</div>
	</section>
</template>

<style lang="scss" scoped src="./Testimonials.scss"></style>
